import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CrackAnalysis, Severity, Finding, InspectionMode, TelemetryPoint, ViewState, GEA_DEFECT_REGISTRY } from '../types';
import { generateInspectionReport } from '../services/pdfService';
import { generateAnnotatedVideo } from '../services/videoService';
import { exportFindingsToZip } from '../services/zipService';
import { LoadingOverlay } from './LoadingOverlay';

interface AnalysisResultsProps {
  image: string;
  analysis: CrackAnalysis;
  onClose: () => void;
  timeline?: CrackAnalysis[];
  telemetryPoints?: TelemetryPoint[];
  onTransferToLab?: (image: string, findings: Finding[], timeline: CrackAnalysis[], allFindings: Finding[]) => void;
  onRerun?: () => void;
  setView?: (v: ViewState) => void;
  customer?: string;
  asset?: string;
}

const severityColors = {
  [Severity.NONE]: 'text-emerald-400',
  [Severity.LOW]: 'text-sky-400',
  [Severity.MEDIUM]: 'text-yellow-400',
  [Severity.HIGH]: 'text-orange-400',
  [Severity.CRITICAL]: 'text-red-400',
};

const boxBorderColors = {
  [Severity.NONE]: 'border-emerald-500',
  [Severity.LOW]: 'border-sky-500',
  [Severity.MEDIUM]: 'border-yellow-500',
  [Severity.HIGH]: 'border-orange-500',
  [Severity.CRITICAL]: 'border-red-500',
};

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  image: initialImage,
  analysis: initialAnalysis,
  onClose,
  timeline,
  telemetryPoints = [],
  onTransferToLab,
  onRerun,
  setView,
  customer,
  asset
}) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);
  const [videoData, setVideoData] = useState<{ url: string; extension: string } | null>(null);
  const [videoProgress, setVideoProgress] = useState(0);

  // State for the currently displayed analysis and image
  const [currentAnalysis, setCurrentAnalysis] = useState<CrackAnalysis>(initialAnalysis);
  const [activeImage, setActiveImage] = useState<string>(initialAnalysis.frameImage || initialImage);

  const [hoveredFindingIdx, setHoveredFindingIdx] = useState<number | null>(null);
  const [selectedFindingIdx, setSelectedFindingIdx] = useState<number | null>(null);
  const [localSelectedFindingIdx, setLocalSelectedFindingIdx] = useState<number | null>(null);
  const [dismissedFindingKeys, setDismissedFindingKeys] = useState<Set<string>>(new Set());

  // Geometric Layout State for alignment
  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number, h: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ w: number, h: number } | null>(null);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const annotatedCanvasRef = useRef<HTMLCanvasElement>(null);
  const ledgerRef = useRef<HTMLDivElement>(null);
  const findingRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Canvas severity colors (hex)
  const canvasColors: Record<string, string> = {
    [Severity.NONE]: '#34d399',
    [Severity.LOW]: '#38bdf8',
    [Severity.MEDIUM]: '#fbbf24',
    [Severity.HIGH]: '#f97316',
    [Severity.CRITICAL]: '#ef4444',
  };

  // Load Image Size (still needed for layout/pan-zoom positioning)
  useEffect(() => {
    const img = new Image();
    img.src = activeImage;
    img.onload = () => setImgNaturalSize({ w: img.width, h: img.height });
  }, [activeImage]);

  // Auto-scroll ledger to selected finding
  useEffect(() => {
    if (selectedFindingIdx !== null && findingRefs.current[selectedFindingIdx]) {
      findingRefs.current[selectedFindingIdx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedFindingIdx]);



  /**
   * Aggregates all unique findings from across the entire mission timeline.
   */
  const allMissionFindings = useMemo(() => {
    const seen = new Set<string>();
    const aggregated: (Finding & { absoluteTime?: number; frameImage?: string; originalAnalysis?: CrackAnalysis; missionKey: string; isDismissed: boolean })[] = [];

    if (timeline && timeline.length > 0) {
      timeline.forEach(t => {
        (t.findings || []).forEach(f => {
          const key = `${f.type}-${f.box.join(',')}`;
          if (!seen.has(key)) {
            seen.add(key);
            aggregated.push({ ...f, absoluteTime: t.timestamp, frameImage: t.frameImage, originalAnalysis: t, missionKey: key, isDismissed: dismissedFindingKeys.has(key) });
          }
        });
      });
      return aggregated;
    }

    return (currentAnalysis.findings || [])
      .map(f => ({ ...f, key: `${f.type}-${f.box.join(',')}` }))
      .map(f => ({
        ...f,
        absoluteTime: currentAnalysis.timestamp,
        frameImage: activeImage,
        originalAnalysis: currentAnalysis,
        missionKey: f.key,
        isDismissed: dismissedFindingKeys.has(f.key)
      }));
  }, [timeline, currentAnalysis, activeImage, dismissedFindingKeys]);

  // ── CANVAS ANNOTATION RENDERER ─────────────────────────────────────────
  // Draws the frame image + all bboxes directly in canvas pixel space.
  useEffect(() => {
    const canvas = annotatedCanvasRef.current;
    if (!canvas || !activeImage) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);

      const findings = currentAnalysis.findings?.filter(
        f => !dismissedFindingKeys.has(`${f.type}-${f.box.join(',')}`)
      ) || [];

      const sc = Math.max(1, canvas.width / 1280);
      findings.forEach((f, idx) => {
        const [ymin, xmin, ymax, xmax] = f.box;
        const x = Math.round((xmin / 1000) * canvas.width);
        const y = Math.round((ymin / 1000) * canvas.height);
        const w = Math.round(((xmax - xmin) / 1000) * canvas.width);
        const h = Math.round(((ymax - ymin) / 1000) * canvas.height);

        const registryEntry = GEA_DEFECT_REGISTRY.find(d => d.type === f.type || d.title === f.type);
        const color = registryEntry?.hex || canvasColors[f.severity] || '#ffffff';
        const isActive = hoveredFindingIdx === idx || localSelectedFindingIdx === idx;

        ctx.strokeStyle = isActive ? color : 'rgba(255,255,255,0.55)';
        ctx.lineWidth = (isActive ? 3 : 1.5) * sc;
        ctx.strokeRect(x, y, w, h);

        const cw = 14 * sc;
        ctx.strokeStyle = color;
        ctx.lineWidth = (isActive ? 3 : 2) * sc;
        ctx.beginPath();
        ctx.moveTo(x, y + cw); ctx.lineTo(x, y); ctx.lineTo(x + cw, y);
        ctx.moveTo(x + w - cw, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + cw);
        ctx.moveTo(x, y + h - cw); ctx.lineTo(x, y + h); ctx.lineTo(x + cw, y + h);
        ctx.moveTo(x + w - cw, y + h); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w, y + h - cw);
        ctx.stroke();

        const fontSize = Math.round(11 * sc);
        ctx.font = `900 ${fontSize}px monospace`;
        const label = `EV_V5.2_${idx + 1}`;
        const textW = ctx.measureText(label).width;
        const pad = 4 * sc;
        const labelH = fontSize + pad * 2;
        ctx.fillStyle = isActive ? color : 'rgba(0,0,40,0.85)';
        ctx.fillRect(x, y - labelH, textW + pad * 2, labelH);
        ctx.fillStyle = isActive ? '#000' : '#fff';
        ctx.fillText(label, x + pad, y - pad);
      });
    };
    img.src = activeImage;
  }, [activeImage, currentAnalysis, hoveredFindingIdx, selectedFindingIdx, dismissedFindingKeys]);

  // Observer Container Size
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Compute exact layout for "contain" fit
  const layout = useMemo(() => {
    if (!imgNaturalSize || !containerSize) return null;
    const { w: cw, h: ch } = containerSize;
    const { w: iw, h: ih } = imgNaturalSize;

    const ratio = Math.min(cw / iw, ch / ih);
    const displayW = iw * ratio;
    const displayH = ih * ratio;

    // Base centered position
    const baseX = (cw - displayW) / 2;
    const baseY = (ch - displayH) / 2;

    return { displayW, displayH, baseX, baseY };
  }, [imgNaturalSize, containerSize]);


  // Sync mission index with local frame finding index for visual highlighting
  useEffect(() => {
    if (selectedFindingIdx !== null && allMissionFindings[selectedFindingIdx]) {
      const target = allMissionFindings[selectedFindingIdx];
      const frameFindings = currentAnalysis.findings || [];
      const matchIdx = frameFindings.findIndex(f =>
        f.box.every((val, i) => val === target.box[i]) && f.type === target.type
      );
      setLocalSelectedFindingIdx(matchIdx !== -1 ? matchIdx : 0);
    } else {
      setLocalSelectedFindingIdx(null);
    }
  }, [selectedFindingIdx, currentAnalysis, allMissionFindings]);

  const toggleFindingDismissal = (key: string) => {
    setDismissedFindingKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredTimeline = useMemo(() => {
    if (!timeline) return undefined;
    return timeline.map(t => ({
      ...t,
      findings: (t.findings || []).filter(f => !dismissedFindingKeys.has(`${f.type}-${f.box.join(',')}`))
    })).filter(t => (t.findings || []).length > 0 || t.detected === false);
  }, [timeline, dismissedFindingKeys]);

  const filteredAnalysis = useMemo(() => {
    return {
      ...currentAnalysis,
      findings: (currentAnalysis.findings || []).filter(f => !dismissedFindingKeys.has(`${f.type}-${f.box.join(',')}`))
    };
  }, [currentAnalysis, dismissedFindingKeys]);

  const formatTime = (seconds?: number) => {
    if (seconds === undefined) return "00:00";
    const totalSecs = Math.round(seconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownloadPdf = async () => {
    // SYNC: Open window immediately to capture user gesture
    const reportWindow = window.open('about:blank', '_blank');
    setIsGeneratingPdf(true);
    try {
      await generateInspectionReport(filteredAnalysis, initialImage, filteredTimeline, "GEA_Technical_Report_", telemetryPoints, 'CUSTOMER', customer, asset, reportWindow);
    } catch (e) {
      if (reportWindow) reportWindow.close();
      console.error(e);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const downloadVideo = (url: string, extension: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `V5.0_Defect_Evidence_${Date.now()}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateVideo = async () => {
    if (!timeline) return;
    setIsGeneratingVideo(true);
    setVideoProgress(0);
    try {
      const result = await generateAnnotatedVideo(timeline, (p) => setVideoProgress(p));
      setVideoData(result);
      downloadVideo(result.url, result.extension); // Auto-download on completion
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!layout || !containerRef.current) return;
    e.preventDefault();

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Current Image Pos (including pan)
    const curX = layout.baseX + offset.x;
    const curY = layout.baseY + offset.y;

    // Mouse relative to image top-left
    const relX = mouseX - curX;
    const relY = mouseY - curY;

    const factor = Math.pow(1.1, -e.deltaY / 150);
    const newScale = Math.min(Math.max(scale * factor, 1), 10);

    // Adjust offset to maintain zoom center
    const newRelX = relX * (newScale / scale);
    const newRelY = relY * (newScale / scale);

    const newOffsetX = mouseX - layout.baseX - newRelX;
    const newOffsetY = mouseY - layout.baseY - newRelY;

    setOffset({ x: newOffsetX, y: newOffsetY });
    setScale(newScale);
  };

  const resetView = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  return (
    <div className="flex flex-col h-full bg-slate-300 animate-fadeIn text-left font-sans">
      {/* Header Bar */}
      <header className="px-8 py-5 bg-[#0000a9] border-b border-white/5 flex items-center justify-between relative">
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-slate-300 animate-pulse"></div>
            <h1 className="text-xl font-black text-white uppercase tracking-[0.2em]">GEA TECHNICAL AUDIT REPORT</h1>
          </div>
          <span className="text-[10px] font-black text-white/60 uppercase tracking-[0.5em] mt-1">
            SECURE_LOG_{currentAnalysis.videoName?.toUpperCase().replace(/\s+/g, '_') || 'GENERIC_SEQUENCE'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-white/60 hover:text-white transition-colors group"
          title="Close Report"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <main className="flex-1 flex overflow-hidden p-6 gap-6">
        <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          {/* Main Visualizer */}
          <div className="flex-1 bg-slate-400 border border-slate-500 relative overflow-hidden group shadow-sm">
            <div
              ref={containerRef}
              onWheel={handleWheel}
              className={`w-full h-full relative ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
              onMouseDown={(e) => { if (scale > 1) { setIsDragging(true); setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); } }}
              onMouseMove={(e) => { if (isDragging) { setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); } }}
              onMouseUp={() => setIsDragging(false)}
            >
              {layout && (
                <div
                  className="absolute transition-transform duration-200"
                  style={{
                    width: layout.displayW,
                    height: layout.displayH,
                    left: layout.baseX + offset.x,
                    top: layout.baseY + offset.y,
                    transform: `scale(${scale})`,
                    transformOrigin: '0 0'
                  }}
                >
                  {/* Canvas-rendered annotations — pixel-perfect bbox alignment */}
                  <canvas
                    ref={annotatedCanvasRef}
                    className="w-full h-full pointer-events-none select-none"
                    style={{ display: 'block' }}
                  />
                </div>
              )}
            </div>

            {/* Viewport Control Layer */}
            <div className="absolute bottom-6 left-6 flex gap-2 pointer-events-auto">
              <button onClick={() => setScale(s => Math.min(s * 1.5, 10))} className="px-6 py-3 bg-[#0000a9] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#0000c0] transition rounded-none shadow-lg">Fit Evidence</button>
              <button onClick={resetView} className="px-6 py-3 bg-slate-300 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:text-[#0000a9] transition border border-slate-400 rounded-none shadow-md">Reset View</button>
            </div>

            {/* Anomaly Navigation */}
            <div className="absolute bottom-6 right-6 flex items-center bg-[#0000a9] border border-white/20 shadow-lg pointer-events-auto overflow-hidden">
              <button
                onClick={() => {
                  const len = allMissionFindings.length;
                  if (len === 0) return;
                  const nextIdx = selectedFindingIdx !== null ? (selectedFindingIdx - 1 + len) % len : len - 1;
                  const f = allMissionFindings[nextIdx];
                  if (f) {
                    setSelectedFindingIdx(nextIdx);
                    if (f.frameImage) setActiveImage(f.frameImage);
                    if (f.originalAnalysis) setCurrentAnalysis(f.originalAnalysis);
                  }
                }}
                className="px-5 py-3 hover:bg-[#000080] transition-colors border-r border-white/10"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div className="px-4 text-[11px] font-black text-white min-w-[3rem] text-center border-r border-white/10 uppercase tracking-widest">
                {selectedFindingIdx !== null ? `${selectedFindingIdx + 1} / ${allMissionFindings.length}` : `- / ${allMissionFindings.length}`}
              </div>
              <button
                onClick={() => {
                  const len = allMissionFindings.length;
                  if (len === 0) return;
                  const nextIdx = selectedFindingIdx !== null ? (selectedFindingIdx + 1) % len : 0;
                  const f = allMissionFindings[nextIdx];
                  if (f) {
                    setSelectedFindingIdx(nextIdx);
                    if (f.frameImage) setActiveImage(f.frameImage);
                    if (f.originalAnalysis) setCurrentAnalysis(f.originalAnalysis);
                  }
                }}
                className="px-5 py-3 hover:bg-[#000080] transition-colors"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          {/* Bottom Cards: Engineering Assessment */}
          <div className="grid grid-cols-12 gap-6 shrink-0 pb-6">
            <div className="col-span-12 bg-slate-300 p-8 border border-slate-400 space-y-6 shadow-sm">
              <h3 className="text-[10px] font-black text-[#0000a9] uppercase tracking-widest">GEA ENGINEERING ASSESSMENT</h3>
              <p className="text-sm font-black text-slate-900 leading-relaxed italic">
                "{currentAnalysis.description}"
              </p>

              <div className="grid grid-cols-2 gap-8 pt-4">
                <div className="space-y-3">
                  <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest leading-none">Technical Remediation</h4>
                  <p className="text-[11px] text-slate-800 leading-relaxed italic font-medium">{currentAnalysis.recommendation}</p>
                </div>
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest leading-none">V5.2 Reliability Index</h4>
                  <div className="space-y-2">
                    <div className="w-full h-1 bg-slate-300 overflow-hidden">
                      <div
                        className="h-full bg-[#0000a9] transition-all duration-1000"
                        style={{ width: `${currentAnalysis.confidenceScore * 100}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-end">
                      <span className="text-[10px] font-black text-slate-900 tracking-tighter">{(currentAnalysis.confidenceScore * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Audit Ledger */}
        <div className="w-[450px] bg-slate-400 border-l border-slate-500 flex flex-col overflow-hidden shadow-2xl z-10">
          <div className="p-8 border-b border-slate-500 flex justify-between items-center bg-slate-300">
            <div>
              <h3 className="text-slate-900 font-black uppercase text-sm tracking-widest italic">Audit Ledger</h3>
              <p className="text-[9px] text-[#0000a9] uppercase tracking-[0.3em] font-black mt-1">Consolidated Master Events</p>
            </div>
            <div className="px-4 py-2 bg-[#0000a9] border border-white/20 text-[10px] font-black text-white rounded-none shadow-sm">
              {allMissionFindings.length} EVENTS
            </div>
          </div>

          <div
            ref={ledgerRef}
            className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar bg-slate-400"
          >
            <div className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic border-b border-slate-500 pb-2">
              {timeline?.length || 1} Distinct Sectors Analyzed.
            </div>

            <div className="space-y-4">
              {allMissionFindings.map((f, i) => (
                <div
                  key={i}
                  ref={el => findingRefs.current[i] = el}
                  onMouseEnter={() => setHoveredFindingIdx(i)}
                  onMouseLeave={() => setHoveredFindingIdx(null)}
                  onClick={() => {
                    setSelectedFindingIdx(i);
                    // Sync the visualizer to the frame this finding originated from
                    if (f.frameImage) setActiveImage(f.frameImage);
                    if (f.originalAnalysis) setCurrentAnalysis(f.originalAnalysis);
                  }}
                  className={`flex overflow-hidden border transition-all cursor-pointer group mb-4 rounded-none relative ${selectedFindingIdx === i ? 'bg-slate-400 border-[#0000a9] shadow-lg scale-[1.02]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/30'}`}
                >
                  {selectedFindingIdx === i && <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#0000a9]"></div>}
                  {/* Snapshot Thumbnail */}
                  <div className="w-32 bg-slate-300 border-r border-slate-400 shrink-0 relative overflow-hidden flex items-center justify-center">
                    {f.frameImage ? (
                      (() => {
                        const [ymin, xmin, ymax, xmax] = f.box || [0, 0, 1000, 1000];
                        const boxW = Math.max(1, xmax - xmin);
                        const boxH = Math.max(1, ymax - ymin);
                        const zoomScale = Math.min(6, 1000 / Math.max(boxW, boxH));
                        // Image is panned so defect center aligns with container center (50%, 50%)
                        // The overlay box is then offset from center by half its own size
                        return (
                          <>
                            <img
                              src={f.frameImage}
                              className="absolute max-w-none origin-top-left"
                              style={{
                                width: `${zoomScale * 100}%`,
                                height: 'auto',
                                left: `${50 - ((xmin + xmax) / 2000) * zoomScale * 100}%`,
                                top: `${50 - ((ymin + ymax) / 2000) * zoomScale * 100}%`,
                                filter: 'contrast(1.05)'
                              }}
                              alt="Finding"
                            />
                            {/* Overlay box aligned to actual defect bbox in zoomed-image space */}
                            <div
                              className="absolute pointer-events-none"
                              style={{
                                left: `${50 - (boxW / 2000) * zoomScale * 100}%`,
                                top: `${50 - (boxH / 2000) * zoomScale * 100}%`,
                                width: `${(boxW / 1000) * zoomScale * 100}%`,
                                height: `${(boxH / 1000) * zoomScale * 100}%`,
                              }}
                            >
                              <div className="absolute inset-0 border border-yellow-400/80 shadow-[0_0_8px_rgba(234,179,8,0.5)]" />
                              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-yellow-400 -translate-x-px -translate-y-px" />
                              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-yellow-400 translate-x-px -translate-y-px" />
                              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-yellow-400 -translate-x-px translate-y-px" />
                              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-yellow-400 translate-x-px translate-y-px" />
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <div className="text-[8px] text-slate-800 font-black uppercase">No Snapshot</div>
                    )}
                  </div>

                  <div className={`flex-1 p-6 ${f.isDismissed ? 'opacity-40 grayscale' : ''}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <h4 className="text-[11px] font-black text-slate-900 uppercase italic tracking-tight">
                          {f.isDismissed && <span className="text-red-500 mr-2">[DISMISSED]</span>}
                          {(() => {
                            const match = GEA_DEFECT_REGISTRY.find(d => d.type === f.type || d.title === f.type);
                            return match ? match.title : f.type;
                          })()}</h4>

                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFindingDismissal(f.missionKey); }}
                          className={`p-1.5 transition-colors rounded-none border ${f.isDismissed ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-red-900/10 text-red-500/50 hover:text-red-500 border-red-500/20'}`}
                          title={f.isDismissed ? "Restore Finding" : "Dismiss Finding"}
                        >
                          {f.isDismissed ? (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-[#0000a9] font-black">{formatTime(f.absoluteTime)}</span>
                      </div>
                    </div>

                    <p className="text-[11px] text-slate-800 italic mb-6 leading-relaxed font-medium">"{f.description}"</p>

                    <div className="space-y-4 pt-4 border-t border-slate-400">
                      <div className="space-y-1">
                        <span className="text-[9px] font-black text-slate-800 uppercase tracking-widest block leading-none">Remedial Advice</span>
                        <p className="text-[10px] text-slate-700 font-black italic">{f.recommendation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar Actions Footer */}
          <div className="p-8 border-t border-slate-500 bg-slate-400 space-y-4">
            <button onClick={handleDownloadPdf} disabled={isGeneratingPdf} className="w-full py-5 bg-[#0000a9] text-white font-black uppercase tracking-[0.2em] text-[11px] transition flex items-center justify-center gap-4 shadow-xl rounded-none">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              {isGeneratingPdf ? 'GENERATING...' : 'AS/IS GEA Technical Report'}
            </button>

            <button
              onClick={async () => {
                if (allMissionFindings.length === 0) return;
                setIsGeneratingZip(true);
                try {
                  await exportFindingsToZip(allMissionFindings, `GEA_Audit_Evidence_${currentAnalysis.videoName || 'Consolidated'}`);
                } catch (e) {
                  alert("Failed to generate ZIP.");
                } finally {
                  setIsGeneratingZip(false);
                }
              }}
              disabled={isGeneratingZip || allMissionFindings.length === 0}
              className="w-full py-5 bg-slate-300 border border-[#0000a9] text-[#0000a9] hover:bg-slate-200 font-black uppercase tracking-[0.2em] text-[11px] transition flex items-center justify-center gap-4 shadow-xl rounded-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              {isGeneratingZip ? 'COMPRESSING...' : 'DOWNLOAD AUDIT ZIP'}
            </button>

            <button
              onClick={() => onTransferToLab?.(activeImage, currentAnalysis.findings || [], timeline || [], allMissionFindings)}
              className="w-full py-5 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase tracking-[0.2em] text-[11px] transition flex items-center justify-center gap-4 shadow-xl rounded-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19.428 15.428a2 2 0 10-2.828-2.828m2.828 2.828l-2.828-2.828m2.828 2.828a2 2 0 11-2.828-2.828m2.828 2.828l-2.828-2.828M5.01 5.01a2 2 0 102.828 2.828M5.01 5.01l2.828 2.828M5.01 5.01a2 2 0 112.828 2.828L5.01 5.01m13.856 0l-2.828 2.828m2.828-2.828a2 2 0 10-2.828 2.828m2.828-2.828a2 2 0 11-2.828 2.828m2.828-2.828l-2.828 2.828M5.01 19.428l2.828-2.828m-2.828 2.828a2 2 0 102.828-2.828m-2.828 2.828a2 2 0 112.828-2.828l-2.828 2.828" /></svg>
              GEA LAB Reporting
            </button>

            {timeline && (
              <button
                onClick={videoData ? () => downloadVideo(videoData.url, videoData.extension) : handleGenerateVideo}
                disabled={isGeneratingVideo}
                className={`w-full py-5 font-black uppercase tracking-[0.2em] text-[11px] transition flex items-center justify-center gap-4 border rounded-none shadow-sm ${videoData ? 'bg-[#0000a9] text-white' : 'bg-slate-300 border-slate-400 text-slate-700'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg>
                {isGeneratingVideo ? `RENDERING (${videoProgress}%)` : (videoData ? `DOWNLOAD MP4` : 'COMPILE VIDEO EVIDENCE')}
              </button>
            )}
          </div>
        </div>
      </main>

      {isGeneratingPdf && <LoadingOverlay message="GENERATING PDF REPORT" subMessage="COMPILING 3D SNAPSHOTS & AUDIT LOGS" />}
      {isGeneratingVideo && <LoadingOverlay message="RENDERING VIDEO EVIDENCE" subMessage={`ENCODING ANNOTATED STREAM ... ${videoProgress}%`} />}
      {isGeneratingZip && <LoadingOverlay message="PREPARING ASSET BUNDLE" subMessage="COMPRESSING HIGH-RESOLUTION EVIDENCE" />}
    </div>
  );
};
